import { Match } from 'effect';
import { isNil } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { useTheme } from 'styled-components';
import type {
  FrequencyEnum,
  HistoricalPortfolioAllocationItem,
  HistoricalPortfolioAllocations,
  HistoricalPortfolioAllocationsTimeSeriesResponse,
} from 'venn-api';
import {
  DownloadableContentBlock,
  type ExcelDataFn,
  formatExportableSubjectWithOptionalFee,
  formatNameWithOptionalFee,
  useAnalysis,
  useAppPrintMode,
  WATERMARK_POSITION_TOP,
} from 'venn-components';
import { useGetRequestSubjectFee } from '../../utils/hooks/use-advisory-fee';
import { blockDisplayHeader, blockExportMetadata, flattenedProxyMapState, vdparFundIdsState } from 'venn-state';

import {
  assertNotNil,
  convertAllocationsToPercentages,
  convertMultipleSeriesDataToExcel,
  type ExcelCell,
  pluralCurry,
} from 'venn-utils';
import { EmptyHeaderSpace } from '../../../content-block/DownloadableContentBlock';
import AreaChart from '../../../studio-blocks/components/charts/AreaChart';
import type { Serie } from '../../../studio-blocks/components/charts/types';
import { EMPTY, getThirdPartyExportMessageRow } from '../../../studio-blocks/logic/exportUtils';
import useExportUpdate from '../../../studio-blocks/logic/useExportUpdate';
import { useSelectedMetrics } from '../../../studio-blocks/logic/useSelectedMetrics';
import { useBlockContext } from '../../contexts/block-context';
import { usePageProxySymbol, usePageVdparSymbol } from '../../components/core/block-subject-footnotes-and-superscripts';

interface HistoricalAllocationsTimeSeriesContentProps {
  id: string;
}

const convertItemsToSeries = ({
  item,
  formatName,
}: {
  item: HistoricalPortfolioAllocationItem;
  formatName: (name: string) => string;
}): Serie => {
  return {
    name: formatName(item.label),
    data: item.allocations.filter(([_, value]) => value !== 0.0),
    type: 'area',
    marker: {
      symbol: 'circle',
    },
  };
};

const getFootnoteContent = ({
  numberOfItemsInOthersGroup,
  numberOfItemsInUncategorizedGroup,
  othersGroupName,
  uncategorizedGroupName,
  pluralizeItems,
  pluralizeInvestments,
}: {
  numberOfItemsInOthersGroup: number;
  numberOfItemsInUncategorizedGroup: number;
  othersGroupName: string | undefined;
  uncategorizedGroupName: string | undefined;
  pluralizeItems: (count: number) => string;
  pluralizeInvestments: (count: number) => string;
}): string | null => {
  if (numberOfItemsInOthersGroup === 0 && numberOfItemsInUncategorizedGroup === 0) {
    return null;
  }

  if (numberOfItemsInOthersGroup === 0 && numberOfItemsInUncategorizedGroup > 0) {
    return `${numberOfItemsInUncategorizedGroup} ${pluralizeInvestments(numberOfItemsInUncategorizedGroup)}\
            been grouped into ${uncategorizedGroupName}.`;
  }

  if (numberOfItemsInOthersGroup > 0 && numberOfItemsInUncategorizedGroup === 0) {
    return `${numberOfItemsInOthersGroup} ${pluralizeItems(numberOfItemsInOthersGroup)}
            been grouped into ${othersGroupName}.`;
  }

  return `${numberOfItemsInUncategorizedGroup} ${pluralizeInvestments(numberOfItemsInUncategorizedGroup)}\
          been grouped into ${uncategorizedGroupName} and\
          ${numberOfItemsInOthersGroup} ${pluralizeItems(numberOfItemsInOthersGroup)}\
          been grouped into ${othersGroupName}.`;
};

const getOthersGroupName = (items: HistoricalPortfolioAllocationItem[]) => {
  return items.find((item) => item.itemType === 'OTHERS')?.label;
};

const getUncategorizedGroupName = (items: HistoricalPortfolioAllocationItem[]) => {
  return items.find((item) => item.itemType === 'UNCATEGORIZED_STRATEGY')?.label;
};

const getPluralizeFunction = (key: keyof HistoricalPortfolioAllocationsTimeSeriesResponse) => {
  return Match.value(key).pipe(
    Match.when('topLevelStrategyAllocations', () => pluralizeStrategies),
    Match.when('fundAllocations', () => pluralizeInvestments),
    Match.exhaustive,
  );
};

const pluralizeStrategies = pluralCurry({
  1: 'strategy has',
  other: 'strategies have',
});

const pluralizeInvestments = pluralCurry({
  1: 'investment has',
  other: 'investments have',
});

const useConvertAllocationsToPercentages = (items: HistoricalPortfolioAllocationItem[]) => {
  return useMemo(() => {
    return convertAllocationsToPercentages(items);
  }, [items]);
};

const useExcelDataFn = ({
  isExportable,
  formattedSubjectName,
  items,
  frequency,
}: {
  isExportable: boolean;
  formattedSubjectName: string;
  items: HistoricalPortfolioAllocationItem[];
  frequency: FrequencyEnum;
}): ExcelDataFn => {
  return useCallback(() => {
    if (!isExportable) {
      return [getThirdPartyExportMessageRow()];
    }

    const subjectNameRow: ExcelCell[] = [
      EMPTY,
      {
        value: formattedSubjectName,
        bold: true,
      },
    ];

    const data = convertMultipleSeriesDataToExcel(
      items.map((item) => ({
        name: item.label,
        series: item.allocations,
      })),
      true,
      undefined,
      frequency,
    );

    return [subjectNameRow, ...data];
  }, [formattedSubjectName, frequency, isExportable, items]);
};

export const HistoricalAllocationsTimeSeriesContent = ({ id }: HistoricalAllocationsTimeSeriesContentProps) => {
  const { blockRef: downloadableContentRef } = useBlockContext();
  const blockHeader = useRecoilValue(blockDisplayHeader(id));
  const { requests, analyses } = useAnalysis(id);
  const { inPrintModeOrReportLab } = useAppPrintMode();
  const exportMetaData = useRecoilValue(blockExportMetadata(id));
  const proxyMap = useRecoilValue(flattenedProxyMapState);
  const proxySymbol = usePageProxySymbol();

  const analysis = analyses[0]![0]!;
  const request = requests[0]!;
  const subject = request.subject;

  const selectedMetric = useSelectedMetrics()[0]!;

  const theme = useTheme();
  const getRequestSubjectFee = useGetRequestSubjectFee();

  const key = assertNotNil(selectedMetric?.metricPath) as keyof HistoricalPortfolioAllocationsTimeSeriesResponse;

  const allocations: HistoricalPortfolioAllocations | undefined =
    analysis?.historicalPortfolioAllocationsTimeSeries?.[0]?.[key];

  const items = allocations?.items ?? [];
  const itemsAsPercentages = useConvertAllocationsToPercentages(items);

  useExportUpdate({
    selectedRefId: id,
    exportMetaData,
    excelDataFn: useExcelDataFn({
      isExportable: analysis.exportable?.[0] ?? false,
      formattedSubjectName: formatExportableSubjectWithOptionalFee(subject, false, getRequestSubjectFee({ subject })),
      items: itemsAsPercentages,
      frequency: request.frequency,
    }),
  });

  const vdparFundIds = useRecoilValue(vdparFundIdsState);
  const vdparSymbol = usePageVdparSymbol();

  if (!allocations || !items) {
    return null;
  }

  const doesItemHaveProxy = (item: HistoricalPortfolioAllocationItem) => {
    return key === 'fundAllocations' && item.itemType === 'DEFAULT' && !isNil(proxyMap?.[item.id]);
  };

  const series = items.map((item) =>
    convertItemsToSeries({
      item,
      formatName: (name) =>
        formatNameWithOptionalFee(
          name,
          proxySymbol,
          vdparSymbol && vdparFundIds.has(item.id) ? vdparSymbol : '',
          undefined,
          doesItemHaveProxy(item),
        ),
    }),
  );

  const footnoteContent = getFootnoteContent({
    numberOfItemsInOthersGroup: allocations.numberOfItemsInOthersGroup,
    numberOfItemsInUncategorizedGroup: allocations.numberOfItemsInUncategorizedGroup,
    othersGroupName: getOthersGroupName(items),
    uncategorizedGroupName: getUncategorizedGroupName(items),
    pluralizeItems: getPluralizeFunction(key),
    pluralizeInvestments,
  });

  const blockContent = (
    <div className="flex flex-col">
      <AreaChart
        theme={theme}
        series={series}
        frequency={request.frequency}
        yAxisUnitFormat="percent"
        stackingEnabled
        className="flex-grow"
      />
      {footnoteContent && (
        <>
          <hr className="h-[1px] m-0 bg-venn-grey-30 border-0" />
          <div className="py-[8px] pr-[8px] pl-[16px] text-[12px]" data-testid="footnote">
            {footnoteContent}
          </div>
        </>
      )}
    </div>
  );

  if (inPrintModeOrReportLab) {
    return blockContent;
  }

  return (
    <DownloadableContentBlock
      header={<EmptyHeaderSpace />}
      noBorder
      downloadable={{
        png: true,
        options: {
          fileName: blockHeader ?? 'Historical Allocations',
          watermark: {
            top: WATERMARK_POSITION_TOP,
            right: 20,
          },
        },
        tracking: {
          description: 'HISTORICAL_PORTFOLIO_ALLOCATIONS_TIMESERIES',
          userUploaded: false,
          relativeToBenchmark: false,
          subjectType: 'portfolio',
          subjectId: request.subject.id,
        },
        disabled: false,
        target: downloadableContentRef,
      }}
    >
      {blockContent}
    </DownloadableContentBlock>
  );
};
